import { createReducer, createAction, PayloadAction } from '@reduxjs/toolkit'

export type MotorbikeStatus = 'in_stock' | 'sold'

export interface MarketPlaceMotorbike {
  id: string
  model: string
  brand_id: number
  brand_name: string
  vin: string | null
  rails_app_id: string | null
  rails_app_hash_id: string | null
  first_registration: string | null
  total_mileage: number | null
  engine_power: number | null
  number_of_previous_owners: number | null
  capacity: number | null
  status: MotorbikeStatus
}

export interface MotorbikeDetail extends MarketPlaceMotorbike {
  metallic: boolean | null
  drive_type: string | null
  gear_box: string | null
  damage_unrepaired: boolean | null
  abs: boolean | null
  heated_grips: boolean | null
  box: boolean | null
  windshield: boolean | null
  next_hu: string | null
  full_service_history: boolean | null
}

export interface MotorbikesReducer {
  motorbikes: MarketPlaceMotorbike[]
  motorbike: MotorbikeDetail | null
  motorbikeRequestId: string | null
  motorbikeLoaded: boolean
  totalPages: number
  currentPage: number
  totalCount: number
  loading: boolean
  error: boolean
  searchTerm: string
  stockTab: MotorbikeStatus
}

const initialState: MotorbikesReducer = {
  motorbikes: [],
  motorbike: null,
  motorbikeRequestId: null,
  motorbikeLoaded: false,
  totalPages: 0,
  currentPage: 1,
  totalCount: 0,
  loading: false,
  error: false,
  searchTerm: '',
  stockTab: 'in_stock'
}

interface CreateMotorbikePayload {
  model: string
  brand_id: number
  vin: string
  motorbike_name?: string | null
  rails_app_id?: string | null
  rails_app_hash_id?: string | null
  first_registration?: string | null
  total_mileage?: string | null
  engine_power?: string | null
  number_of_previous_owners?: string | null
  capacity?: string | null
  metallic?: boolean | null
  drive_type?: string | null
  gear_box?: string | null
  damage_unrepaired?: boolean | null
  abs?: boolean | null
  heated_grips?: boolean | null
  box?: boolean | null
  windshield?: boolean | null
  next_hu?: string | null
  full_service_history?: boolean | null
}

interface UpdateMotorbikePayload {
  id: string
  model: string
  brand_id: number
  vin: string
  motorbike_name: string | null
  rails_app_id: string | null
  rails_app_hash_id: string | null
  first_registration: string | null
  total_mileage: string | null
  engine_power: string | null
  number_of_previous_owners: string | null
  capacity: string | null
  metallic: boolean | null
  drive_type: string | null
  gear_box: string | null
  damage_unrepaired: boolean | null
  abs: boolean | null
  heated_grips: boolean | null
  box: boolean | null
  windshield: boolean | null
  next_hu: string | null
  full_service_history: boolean | null
}

export type CreateMotorbikeActionType = PayloadAction<CreateMotorbikePayload>
export type UpdateMotorbikeActionType = PayloadAction<UpdateMotorbikePayload>

export const CREATE_MOTORBIKE = createAction<CreateMotorbikePayload>('motorbikes_reducer/create_motorbike')
export const CREATE_MOTORBIKE_SUCCESS = createAction('motorbikes_reducer/create_motorbike_success')
export const CREATE_MOTORBIKE_FAILURE = createAction('motorbikes_reducer/create_motorbike_failure')

export const UPDATE_MOTORBIKE = createAction<UpdateMotorbikePayload>('motorbikes_reducer/update_motorbike')
export const UPDATE_MOTORBIKE_SUCCESS = createAction('motorbikes_reducer/update_motorbike_success')
export const UPDATE_MOTORBIKE_FAILURE = createAction('motorbikes_reducer/update_motorbike_failure')

export const DELETE_MOTORBIKE = createAction<string>('motorbikes_reducer/delete_motorbike')
export const DELETE_MOTORBIKE_SUCCESS = createAction<string>('motorbikes_reducer/delete_motorbike_success')
export const DELETE_MOTORBIKE_FAILURE = createAction('motorbikes_reducer/delete_motorbike_failure')

interface FetchMotorbikesSuccessPayload {
  motorbikes: MarketPlaceMotorbike[]
  totalPages: number
  totalCount: number
}

export const FETCH_MOTORBIKE = createAction<string>('motorbikes_reducer/fetch_motorbike')
export const FETCH_MOTORBIKE_SUCCESS = createAction<MotorbikeDetail>('motorbikes_reducer/fetch_motorbike_success')
export const FETCH_MOTORBIKE_FAILURE = createAction('motorbikes_reducer/fetch_motorbike_failure')
export const RESET_MOTORBIKE = createAction('motorbikes_reducer/reset_motorbike')

export const FETCH_MOTORBIKES = createAction('motorbikes_reducer/fetch_motorbikes')
export const FETCH_MOTORBIKES_SUCCESS = createAction<FetchMotorbikesSuccessPayload>(
  'motorbikes_reducer/fetch_motorbikes_success'
)
export const FETCH_MOTORBIKES_FAILURE = createAction('motorbikes_reducer/fetch_motorbikes_failure')
export const SET_CURRENT_PAGE = createAction<number>('motorbikes_reducer/set_current_page')
export const SET_SEARCH_TERM = createAction<string>('motorbikes_reducer/set_search_term')
export const SET_STOCK_TAB = createAction<MotorbikeStatus>('motorbikes_reducer/set_stock_tab')
interface SetMotorbikeStatusPayload {
  id: string
  status: MotorbikeStatus
  name?: string
}

export const SET_MOTORBIKE_STATUS = createAction<SetMotorbikeStatusPayload>('motorbikes_reducer/set_motorbike_status')
export const SET_MOTORBIKE_STATUS_SUCCESS = createAction('motorbikes_reducer/set_motorbike_status_success')
export const SET_MOTORBIKE_STATUS_FAILURE = createAction('motorbikes_reducer/set_motorbike_status_failure')

export const motorbikesReducer = createReducer(initialState, (builder) => {
  builder.addCase(CREATE_MOTORBIKE, (state) => {
    return { ...state, loading: true, error: false }
  })
  builder.addCase(CREATE_MOTORBIKE_SUCCESS, (state) => {
    return { ...state, loading: false, error: false }
  })
  builder.addCase(CREATE_MOTORBIKE_FAILURE, (state) => {
    return { ...state, loading: false, error: true }
  })
  builder.addCase(UPDATE_MOTORBIKE, (state) => {
    return { ...state, loading: true, error: false }
  })
  builder.addCase(UPDATE_MOTORBIKE_FAILURE, (state) => {
    return { ...state, loading: false, error: true }
  })
  builder.addCase(DELETE_MOTORBIKE, (state) => {
    return { ...state, loading: true, error: false }
  })
  builder.addCase(DELETE_MOTORBIKE_SUCCESS, (state, action) => {
    return {
      ...state,
      motorbikes: state.motorbikes.filter((motorbike) => motorbike.id !== action.payload),
      loading: false,
      error: false
    }
  })
  builder.addCase(DELETE_MOTORBIKE_FAILURE, (state) => {
    return { ...state, loading: false, error: true }
  })
  builder.addCase(FETCH_MOTORBIKE, (state, action) => {
    return { ...state, motorbike: null, motorbikeRequestId: action.payload, motorbikeLoaded: false }
  })
  builder.addCase(FETCH_MOTORBIKE_SUCCESS, (state, action) => {
    return { ...state, motorbike: action.payload, motorbikeLoaded: true }
  })
  builder.addCase(FETCH_MOTORBIKE_FAILURE, (state) => {
    return { ...state, motorbike: null, motorbikeLoaded: true }
  })
  builder.addCase(RESET_MOTORBIKE, (state) => {
    return { ...state, motorbike: null, motorbikeRequestId: null, motorbikeLoaded: false }
  })
  builder.addCase(FETCH_MOTORBIKES, (state) => {
    return { ...state, loading: true, error: false }
  })
  builder.addCase(FETCH_MOTORBIKES_SUCCESS, (state, action) => {
    return {
      ...state,
      motorbikes: action.payload.motorbikes,
      totalPages: action.payload.totalPages,
      totalCount: action.payload.totalCount,
      loading: false,
      error: false
    }
  })
  builder.addCase(FETCH_MOTORBIKES_FAILURE, (state) => {
    return { ...state, motorbikes: [], loading: false, error: true }
  })
  builder.addCase(SET_CURRENT_PAGE, (state, action) => {
    return { ...state, currentPage: action.payload }
  })
  builder.addCase(SET_SEARCH_TERM, (state, action) => {
    return { ...state, searchTerm: action.payload }
  })
  builder.addCase(SET_STOCK_TAB, (state, action) => {
    return { ...state, stockTab: action.payload, currentPage: 1 }
  })
  builder.addCase(SET_MOTORBIKE_STATUS, (state) => {
    return { ...state, loading: true, error: false }
  })
  builder.addCase(SET_MOTORBIKE_STATUS_SUCCESS, (state) => {
    return { ...state, loading: false, error: false }
  })
  builder.addCase(SET_MOTORBIKE_STATUS_FAILURE, (state) => {
    return { ...state, loading: false, error: true }
  })
})
